import { useState } from "react";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import { createEnquiry } from "../services/enquiryService";
import { footerLinkGroups, policyLinks } from "../config/siteNavigation";
import { routePaths } from "../constants/routes";
import logo from "../assets/qacoder-logo.svg";
import "../style/footer.css";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (event) => {
    event.preventDefault();

    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email.trim())) {
      toast.error("Please enter a valid email for updates.");
      return;
    }

    setLoading(true);

    try {
      await createEnquiry(
        {
          name: "Newsletter Subscriber",
          email: email.trim().toLowerCase(),
          phone: "",
          enquiryType: "newsletter",
          subject: "Newsletter signup",
          message: "User subscribed from footer newsletter form.",
        },
        {
          source: "footer-newsletter",
          enquiryType: "newsletter",
        }
      );

      toast.success("Subscribed successfully.");
      setEmail("");
    } catch (error) {
      toast.error(error.message || "Unable to subscribe right now.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="footer">
      <div className="footer-cta">
        <div>
          <span>Need help choosing a QA path or product flow?</span>
          <h2>Jump into the ecommerce demo, or book a free QA course demo today.</h2>
        </div>
        <div className="footer-cta-actions">
          <NavLink to={routePaths.product}>Open Ecommerce Demo</NavLink>
          <NavLink to="/enroll">Join Course</NavLink>
          <NavLink to="/contact">Send Enquiry</NavLink>
        </div>
      </div>

      <div className="footer-grid">
        <div className="footer-brand">
          <div className="footer-logo">
            <img src={logo} alt="QA Coder logo" className="footer-logo-image" />
          </div>

          <p>
            QA Coder helps learners build practical manual testing, automation testing,
            API testing, interview, project, and ecommerce workflow skills for real QA careers.
          </p>

          <form className="footer-newsletter" onSubmit={handleNewsletterSubmit}>
            <label htmlFor="footer-email">Get study notes and course updates</label>
            <div>
              <input
                id="footer-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Enter your email"
              />
              <button type="submit" disabled={loading}>
                {loading ? "Saving..." : "Subscribe"}
              </button>
            </div>
          </form>

          <div className="footer-social">
            <a href="https://www.linkedin.com/in/qa-coder-708418404/" target="_blank" rel="noreferrer" className="linkedin" aria-label="LinkedIn">
              <i className="fab fa-linkedin-in"></i>
            </a>
            <a href="mailto:qacoder0@gmail.com" className="email" aria-label="Email">
              <i className="fas fa-envelope"></i>
            </a>
            <a href="https://www.instagram.com/qacoder0/?hl=en" target="_blank" rel="noreferrer" className="instagram" aria-label="Instagram">
              <i className="fab fa-instagram"></i>
            </a>
            <a href="https://www.youtube.com/@QACoder" target="_blank" rel="noreferrer" className="youtube" aria-label="YouTube">
              <i className="fab fa-youtube"></i>
            </a>
          </div>
        </div>

        {footerLinkGroups.map((group) => (
          <FooterColumn title={group.title} links={group.links} key={group.title} />
        ))}

        <div className="footer-col footer-contact-col">
          <h4>Contact and Policies</h4>
          <ul>
            <li><a href="mailto:qacoder0@gmail.com">qacoder0@gmail.com</a></li>
            <li><NavLink to="/contact">Send Enquiry</NavLink></li>
            {policyLinks.map((link) => (
              <li key={link.label}><NavLink to={link.to}>{link.label}</NavLink></li>
            ))}
          </ul>
        </div>
      </div>

      <div className="footer-bottom">
        <p>
          Copyright {new Date().getFullYear()} <span>QA Coder IT Coaching</span>. All rights reserved.
        </p>
        <p>Built for software testing learners, job seekers, and QA professionals.</p>
      </div>
    </footer>
  );
}

function FooterColumn({ title, links }) {
  return (
    <div className="footer-col">
      <h4>{title}</h4>
      <ul>
        {links.map((link) => (
          <li key={link.label}>
            <NavLink to={link.to}>{link.label}</NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}
